import { ForwardRefRenderFunction, MouseEvent, forwardRef, useImperativeHandle } from 'react'

import { EDITOR_DRAG_DETECTION_THRESHOLD } from '@editor/config/constants'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import { Mode, SceneEventHandlers, Steps, Tools, ViewSides } from '../definitions'
import { setCurrentMode } from '../store'

export interface EventHandlersForwardRef {
  onDown: (ev: MouseEvent<HTMLDivElement>) => void
  onUp: (ev: MouseEvent<HTMLDivElement>) => void
  onClick: (ev: MouseEvent<HTMLDivElement>) => void
  onMove: (ev: MouseEvent<HTMLDivElement>) => void
}
interface EventHandlersProps {
  leftSceneRef: React.RefObject<SceneEventHandlers>
  rightSceneRef: React.RefObject<SceneEventHandlers>
  mainSceneRef: React.RefObject<SceneEventHandlers>
  fineTuneSceneRef: React.RefObject<SceneEventHandlers>
}

let onDownData: { position: number[]; timestamp: number } | undefined
// let lastTouchClient: { x: number; y: number } | undefined

/**
 * Handle events separately from the index file so we can avoid subscribing
 * to states that could cause unnecessary re-renders such as currentSide.
 */
const EventHandlersFn: ForwardRefRenderFunction<EventHandlersForwardRef, EventHandlersProps> = (
  { leftSceneRef, rightSceneRef, fineTuneSceneRef, mainSceneRef },
  ref,
) => {
  // Store
  const dispatch = useAppDispatch()
  const currentSide = useSelector((state: RootState) => state.pcdAlignment.currentSide)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)
  const currentMode = useSelector((state: RootState) => state.pcdAlignment.currentMode)

  useImperativeHandle(ref, () => {
    const getActiveScene = () => {
      if (currentTool === Tools.Overview) {
        return mainSceneRef.current
      }
      if (currentStep === Steps.FineTuneOrSave) {
        return fineTuneSceneRef.current
      }
      if (currentSide === ViewSides.Left) {
        return leftSceneRef.current
      }

      return rightSceneRef.current
    }

    return {
      onDown: (ev) => {
        if (currentTool !== Tools.PCDAlignment || ev.button !== 0) return
        onDownData = { position: [ev.clientX, ev.clientY], timestamp: Date.now() }
      },
      onUp: (ev) => {
        const activeScene = getActiveScene()
        if (ev.button === 1 && activeScene?.onFocus) {
          activeScene?.onFocus(ev)
        }
      },
      onClick: (ev) => {
        const activeScene = getActiveScene()

        // User trying to focus using tool
        if (currentMode === Mode.Focus && activeScene?.onFocus) {
          activeScene?.onFocus(ev)
          dispatch(setCurrentMode(Mode.Normal))
          return
        }

        // There are only click events on the PCD alignment tool
        if (currentTool !== Tools.PCDAlignment) return

        // Prevent click events on drag
        const distanceFromDown = onDownData
          ? Math.hypot(ev.clientX - onDownData.position[0], ev.clientY - onDownData.position[1])
          : 0
        const isDragged = distanceFromDown > EDITOR_DRAG_DETECTION_THRESHOLD
        onDownData = undefined
        if (isDragged) return

        // Send click event to the active scene
        if (activeScene?.onClick) activeScene.onClick(ev)
      },
      onMove: (ev) => {
        const activeScene = getActiveScene()

        if (!activeScene?.onMove) return

        // Send move event to the active scene
        activeScene.onMove(ev)
      },
    }
  }, [
    currentTool,
    currentMode,
    currentSide,
    currentStep,
    leftSceneRef,
    rightSceneRef,
    mainSceneRef,
    fineTuneSceneRef,
    dispatch,
  ])

  // Nothing to render here, just handle events
  return null
}

const EventHandlers = forwardRef(EventHandlersFn)

export default EventHandlers
